'use client';

import { faChevronDown } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cn } from '@shared/utils/utils';
import React, { useCallback, useEffect, useRef, useState } from 'react';

export interface RadioOption {
  value: string;
  label: string;
  description?: string;
}

interface RadioSelectProps {
  options: RadioOption[];
  value?: string;
  setValue: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  dataTestId?: string;
  dropdownWidth?: 'auto' | 'trigger' | 'sm' | 'md' | 'lg' | 'xl';
}

/**
 * 🎯 RadioSelect - Componente de selección con radio buttons y descripciones
 *
 * Combina la funcionalidad de un select con radio buttons, mostrando
 * descripciones para cada opción y un indicador visual de selección.
 *
 * Características:
 * - Dropdown con opciones en formato radio
 * - Descripciones opcionales para cada opción
 * - Indicador visual de selección (check)
 * - Diseño con bordes redondeados
 * - Soporte para temas light/dark
 * - Control de ancho del dropdown (auto, trigger, sm, md, lg, xl)
 */
const RadioSelect = React.memo(
  ({
    options,
    value,
    setValue,
    placeholder = 'Seleccionar...',
    disabled = false,
    className,
    dataTestId,
    dropdownWidth = 'auto',
  }: RadioSelectProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState<'left' | 'right'>('left');
    const dropdownRef = useRef<HTMLDivElement>(null);

    // 🎯 Calcular posición del dropdown basado en el espacio disponible
    const calculateDropdownPosition = useCallback(() => {
      if (!dropdownRef.current) return;

      const rect = dropdownRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;

      // Obtener el ancho real del dropdown basado en la prop
      let estimatedDropdownWidth = 350; // default
      switch (dropdownWidth) {
        case 'sm':
          estimatedDropdownWidth = 250;
          break;
        case 'md':
          estimatedDropdownWidth = 350;
          break;
        case 'lg':
          estimatedDropdownWidth = 500;
          break;
        case 'xl':
          estimatedDropdownWidth = 600;
          break;
        default:
          estimatedDropdownWidth = 400;
      }

      // Calcular si hay suficiente espacio a la derecha
      const spaceOnRight = viewportWidth - rect.left - estimatedDropdownWidth;
      const spaceOnLeft = rect.left;

      // Usar right-0 si hay más espacio a la izquierda o si se corta por la derecha
      const shouldUseRight = spaceOnRight < 20 || spaceOnLeft > spaceOnRight;
      setDropdownPosition(shouldUseRight ? 'right' : 'left');
    }, [dropdownWidth]);

    // 🎯 Cerrar dropdown al hacer clic fuera
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          setIsOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // 🎯 Recalcular posición cuando cambie el tamaño de la ventana
    useEffect(() => {
      const handleResize = () => {
        if (isOpen) {
          calculateDropdownPosition();
        }
      };

      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, [isOpen, calculateDropdownPosition]);

    // 🎯 Manejar apertura del dropdown
    const handleToggleDropdown = () => {
      if (!disabled) {
        const newIsOpen = !isOpen;
        setIsOpen(newIsOpen);

        // Calcular posición cuando se abre
        if (newIsOpen) {
          setTimeout(calculateDropdownPosition, 0);
        }
      }
    };

    // 🎯 Manejar selección de opción
    const handleOptionSelect = (optionValue: string) => {
      setValue(optionValue);
      setIsOpen(false);
    };

    // 🎯 Obtener opción seleccionada para mostrar
    const selectedOption = options.find((option) => option.value === value);

    // 🎯 Determinar el ancho del dropdown basado en la prop
    const getDropdownWidthClass = () => {
      switch (dropdownWidth) {
        case 'trigger':
          return 'w-full';
        case 'sm':
          return 'min-w-[200px] max-w-[250px]';
        case 'md':
          return 'min-w-[300px] max-w-[350px]';
        case 'lg':
          return 'min-w-[400px] max-w-[500px]';
        case 'xl':
          return 'min-w-[500px] max-w-[600px]';
        case 'auto':
        default:
          return 'min-w-[300px] max-w-[400px]';
      }
    };

    return (
      <div className="relative" ref={dropdownRef} data-testid={dataTestId}>
        {/* 🎯 Trigger del dropdown */}
        <button
          type="button"
          onClick={handleToggleDropdown}
          disabled={disabled}
          className={cn(
            'flex items-center justify-between w-full px-3 py-2.5 text-sm gap-3',
            'bg-card-bg border border-card-border rounded-md',
            'hover:border-hover-press focus:border-primary',
            'transition-colors duration-200',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            className,
          )}
          data-testid={`${dataTestId}-trigger`}
        >
          <span className={cn('text-left', selectedOption ? 'text-text' : 'text-subtext')}>
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <FontAwesomeIcon
            icon={faChevronDown}
            className={cn('w-3 h-3 text-icons transition-transform duration-200', isOpen && 'rotate-180')}
          />
        </button>

        {/* 🎯 Dropdown con opciones */}
        {isOpen && (
          <div
            className={cn('absolute top-full z-50 mt-1 min-w-full', dropdownPosition === 'left' ? 'left-0' : 'right-0')}
          >
            <div
              className={cn(
                'bg-card-bg border border-card-border rounded-md shadow-lg overflow-hidden',
                getDropdownWidthClass(),
              )}
            >
              <div className="p-1.5 max-h-60 overflow-y-auto space-y-2">
                {options.map((option) => (
                  <div key={option.value} className="relative">
                    <button
                      type="button"
                      onClick={() => handleOptionSelect(option.value)}
                      className={cn(
                        'w-full px-4 py-4 text-left hover:bg-hover-press bg-card-bg',
                        'transition-colors duration-150',
                        'border border-card-border rounded-md',
                        'focus:outline-none focus:bg-hover-press',
                      )}
                      data-testid={`${dataTestId}-option-${option.value}`}
                    >
                      <div className="flex items-center gap-3 w-full">
                        {/* 🎯 Contenido de la opción */}
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-title text-sm">{option.label}</div>
                          {option.description && (
                            <div className="text-xs text-title mt-1 text-regular-normal">{option.description}</div>
                          )}
                        </div>

                        {/* 🎯 Radio button visual */}
                        <div className="flex-shrink-0 mt-0.5">
                          <div
                            className={cn(
                              'w-3 h-3 rounded-full border flex items-center justify-center',
                              'transition-colors duration-150',
                              value === option.value ? 'border-icons p-0.5' : 'border-icons ',
                            )}
                          >
                            {value === option.value && <div className="w-full h-full rounded-full bg-icons" />}
                          </div>
                        </div>
                      </div>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  },
);

RadioSelect.displayName = 'RadioSelect';

export default RadioSelect;
